import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringToolsContainer } from "../../core/StringToolsContainer";
import { StringObject } from "../../objects/StringObject";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";
import LerpVector2Tool from "../../tools/LerpVector2Tool";

/**
 * The `StringProgress` class extends the `StringModule` class and is responsible for
 * managing progress-based behavior for elements during scroll events. It maps specific
 * attributes, initializes objects, and updates their progress based on scroll data.
 */
export class StringProgress extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "progress";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
    ];
  }

  /**
   * Called when an object is initialized.
   */
  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
  }

  /**
   * Called on scroll.
   */
  onScroll(data: StringData): void {
    super.onScroll(data);
    this.objects.forEach((object) => {
      this.setUpObject(object);
    });
  }

  onObjectConnected(object: StringObject) {
    super.onObjectConnected(object);

    object.setProperty("setUpObject", this.setUpObject.bind(this));
    object.events.on("enter", object.getProperty<(object: StringObject) => void>("setUpObject"));
  }

  private setUpObject(object: StringObject) {
    const startPosition = object.getProperty<number>("start-position");
    const differencePosition = object.getProperty<number>("difference-position");
    const key = object.getProperty<string>("key");
    const progress = object.getProperty<EasingFunctionOutput>("easing")(
      Math.min(
        1,
        Math.max(0, (this.data.scroll.transformedCurrent - startPosition) / differencePosition)
      )
    );
    if (object.getProperty<number>("progress") !== progress) {
      this.events.emit(`object:progress:${object.id}`, progress);
      object.setProperty<number>("progress", progress);
      const progressStr = progress.toString();
      this.applyToElementAndConnects(object, (el) => {
        el.style.setProperty(key, progressStr);
      });
    }
  }
}
